import { useTranslation } from 'react-i18next';

/**
 * Hook to get the correct content field based on current language
 * For use in frontend components fetching from database
 */
export const useMultilingualContent = () => {
  const { i18n } = useTranslation();

  /**
   * Get the value for current language from a database row
   * Example: getContentValue(row, 'content_value') returns row.content_value_en if language is 'en'
   */
  const getContentValue = (row: any, baseField: string): string => {
    if (!row) return '';

    // Normalize language code (e.g., 'en-US' -> 'en')
    const currentLang = i18n.language?.split('-')[0] || 'pt';

    if (currentLang === 'en') {
      return row[`${baseField}_en`] || row[baseField] || '';
    } else if (currentLang === 'es') {
      return row[`${baseField}_es`] || row[baseField] || '';
    }

    return row[baseField] || '';
  };

  /**
   * Transform site_content rows into object with language-aware values
   */
  const transformContent = (data: Array<{
    content_key: string;
    content_value: string;
    content_value_en?: string | null;
    content_value_es?: string | null;
  }>): Record<string, string> => {
    const result: Record<string, string> = {};

    // Normalize language code (e.g., 'en-US' -> 'en')
    const currentLang = i18n.language?.split('-')[0] || 'pt';

    data.forEach((item) => {
      if (currentLang === 'en') {
        result[item.content_key] = item.content_value_en || item.content_value || '';
      } else if (currentLang === 'es') {
        result[item.content_key] = item.content_value_es || item.content_value || '';
      } else {
        result[item.content_key] = item.content_value || '';
      }
    });

    return result;
  };

  return {
    getContentValue,
    transformContent,
    currentLanguage: i18n.language,
  };
};
